import React, { useState, useEffect, useCallback } from 'react';
import './Home.css';
import '../AdminComponents/DashboardHeader.css';
import { useNavigate, useLocation, NavLink } from 'react-router-dom';
import { getAuth, signOut } from 'firebase/auth';
import { getDatabase, ref as dbRef, onValue } from 'firebase/database';
import GenerativeOrbAnimation from './GenerativeOrbAnimation';
import { useActiveWorld } from '../hooks/useActiveWorld';
import { useDealsAccess } from '../hooks/useDealsAccess';
import { useTeam } from '../contexts/TeamContext';
import TeamSelector from '../components/TeamSelector';
import TeamSwitcher from '../components/TeamSwitcher';

const Home = ({ showDashboard = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const auth = getAuth();
  const db = getDatabase();
  const [userPhoto, setUserPhoto] = useState('');
  const [userName, setUserName] = useState('');
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [permissions, setPermissions] = useState({
    hasSalesPages: false,
    hasSalesGod: false,
    hasXRAAY: false,
    hasAcademy: false,
  });

  // Page transition
  const [isExiting, setIsExiting] = useState(false);

  // Team System
  const { 
    features: teamFeatures, 
    accountType, 
    isOwner: isOwnerAccount,
    hasFeature 
  } = useActiveWorld();
  
  const {
    availableTeams,
    pendingInvitations,
    activeTeam,
    isLoading: isTeamLoading,
    isInitialized: isTeamInitialized,
    multiInstanceEnabled,
    mySubWorlds,
    isSubWorldSession,
    returnToParentAccount,
    isSwitching: isTeamSwitching,
  } = useTeam();
  const [isReturningToParent, setIsReturningToParent] = useState(false);

  // Der Deal Wizard hängt an der aktiven World (Owner-Lizenz + Team-Feature),
  // nicht am eigenen Users-Datensatz — daher separat vom `permissions`-State.
  const { hasAccess: hasDealsAccess } = useDealsAccess();

  const isTeamMemberWithLimitedAccess = accountType === 'member' && !teamFeatures.includes('all');
  const hasLeadsOnlyAccess = isTeamMemberWithLimitedAccess && 
    teamFeatures.includes('leads') && 
    !teamFeatures.includes('inbox') && 
    !teamFeatures.includes('offers') && 
    !teamFeatures.includes('settings');

  const needsAccountSelector = () => {
    if (!isTeamInitialized || isTeamLoading) return false;
    const hasPendingInvites = pendingInvitations && pendingInvitations.length > 0;
    const hasMultiAccounts = availableTeams && availableTeams.length > 1;
    const isTeamMember = accountType === 'member';
    // Multi-Instance: User soll Zugriff auf TeamSelector bekommen, sobald das Feature
    // aktiv ist (zum Anlegen der ersten Sub-World) oder bereits Sub-Worlds existieren.
    // Sub-World-Sessions selbst (parentOwnerUid gesetzt) sehen den Selector nicht
    // automatisch — sie sind ja bereits in einer Instanz und gehen direkt zum Dashboard.
    const hasSubWorldAccess = !isSubWorldSession && (
      multiInstanceEnabled === true ||
      (Array.isArray(mySubWorlds) && mySubWorlds.length > 0)
    );
    return isTeamMember || hasPendingInvites || hasMultiAccounts || hasSubWorldAccess;
  };

  useEffect(() => {
    if (isTeamInitialized && !isTeamLoading) {
      const isOnRoot = location.pathname === '/';
      const isOnDashboard = location.pathname === '/dashboard';
      
      if (isOnRoot && !needsAccountSelector()) {
        navigate('/dashboard', { replace: true });
      } else if (isOnDashboard && needsAccountSelector() && !activeTeam) {
        navigate('/', { replace: true });
      }
    }
  }, [isTeamInitialized, isTeamLoading, location.pathname, accountType, availableTeams, pendingInvitations, activeTeam, multiInstanceEnabled, mySubWorlds, isSubWorldSession]);

  const handleTeamSelected = (teamId, route) => {
    navigate(route || '/dashboard');
  };

  useEffect(() => {
    if (auth.currentUser) {
      setUserName(auth.currentUser.displayName || 'User');

      const userRef = dbRef(db, `Users/${auth.currentUser.uid}`);
      const unsubscribe = onValue(userRef, (snapshot) => {
        const userData = snapshot.val();
        if (userData) {
          if (userData.userPhoto) {
            setUserPhoto(userData.userPhoto);
          }
          if (userData.name) {
            setUserName(userData.name);
          } else if (userData.firstName) {
            setUserName(`${userData.firstName} ${userData.lastName || ''}`.trim());
          }
          
          let hasWorldAccess = false;
          
          if (userData.teamMemberships) {
            hasWorldAccess = Object.values(userData.teamMemberships).some(membership => 
              membership.status === 'accepted' &&
              membership.features?.some(f => ['leads', 'inbox', 'offers', 'all'].includes(f))
            );
          }
          
          if (!hasWorldAccess && userData.teamMembership?.features) {
            hasWorldAccess = userData.teamMembership.features.some(f => 
              ['leads', 'inbox', 'offers', 'all'].includes(f)
            );
          }
          
          setPermissions({
            hasSalesPages: userData.hasSalesPages || false,
            hasSalesGod: userData.hasSalesGod || hasWorldAccess || false,
            hasXRAAY: userData.hasXRAAY || false,
            hasAcademy: userData.hasAcademy || false,
          });
        }
      });

      return () => unsubscribe();
    }
  }, [auth.currentUser, db]);

  const navigateWithTransition = useCallback((route) => {
    if (isExiting) return;
    setIsExiting(true);
    setTimeout(() => navigate(route), 350);
  }, [isExiting, navigate]);

  const handleNavigateToPages = () => {
    if (permissions.hasSalesPages) navigateWithTransition('/pages');
  };

  const handleNavigateToWorld = () => {
    if (permissions.hasSalesGod) {
      navigateWithTransition(hasLeadsOnlyAccess ? '/leadswipe' : '/world');
    }
  };

  const handleNavigateToXray = () => {
    if (permissions.hasXRAAY) navigateWithTransition('/business-analysis');
  };

  const handleNavigateToAcademy = () => {
    if (permissions.hasAcademy) navigateWithTransition('/academy');
  };

  const handleNavigateToDealWizard = () => {
    if (hasDealsAccess) navigateWithTransition('/deals');
  };

  const toggleProfileMenu = () => {
    setShowProfileMenu(!showProfileMenu);
  };

  // Back-Button-Logik: aus dem Dashboard zurück zur Account-Auswahl.
  //  - In einer Sub-World-Session: wir sind durch ein Custom-Token im Sub-Account
  //    eingeloggt; der Selector ist nicht erreichbar, ohne vorher zum Parent
  //    zurückzukehren. Daher returnToParentAccount() → onAuthStateChanged lädt
  //    die Parent-Daten neu, danach landen wir per navigate('/') auf dem Picker.
  //  - Sonst (Parent mit Multi-Instance/Multi-Account): einfach zu '/' navigieren,
  //    der Effekt-Hook entscheidet zwischen Selector und Direkt-Redirect.
  // Nicht gerendert, wenn es nichts zu wechseln gibt (Single-Account-User).
  const hasMultiAccountChoice = (
    (Array.isArray(availableTeams) && availableTeams.length > 1) ||
    (Array.isArray(pendingInvitations) && pendingInvitations.length > 0) ||
    multiInstanceEnabled === true ||
    (Array.isArray(mySubWorlds) && mySubWorlds.length > 0)
  );
  const canShowBackButton = isSubWorldSession || hasMultiAccountChoice;

  const handleBackToAccounts = useCallback(async () => {
    if (isReturningToParent || isTeamSwitching) return;
    if (isSubWorldSession) {
      setIsReturningToParent(true);
      try {
        const ok = await returnToParentAccount();
        if (ok) {
          // Direkt auf den Picker. Falls der Parent nur einen Account hat,
          // routet der Effekt-Hook in Home automatisch wieder zu /dashboard.
          navigate('/', { replace: true });
        }
      } finally {
        setIsReturningToParent(false);
      }
      return;
    }
    navigate('/', { replace: true });
  }, [isSubWorldSession, returnToParentAccount, isReturningToParent, isTeamSwitching, navigate]);

  const handleSignOut = () => {
    signOut(auth).then(() => {
      console.log("Successfully signed out!");
      navigate('/login');
    }).catch((error) => {
      console.error("Error signing out:", error);
    });
  };

  // Loading
  if (!isTeamInitialized || isTeamLoading) {
    return (
      <div className="home-main-container">
        <GenerativeOrbAnimation />
        <div className="home-loading">
          <div className="home-loading-spinner"></div>
        </div>
      </div>
    );
  }

  // Team Selector
  const isOnRoot = location.pathname === '/';
  if (isOnRoot && needsAccountSelector()) {
    return <TeamSelector onSelectTeam={handleTeamSelected} />;
  }

  return (
    <div className={`home-main-container ${isExiting ? 'home-page-exit' : 'home-page-enter'}`}>
      <GenerativeOrbAnimation />
      
      {/* Navigation Header */}
      <div className="home-navigation-header">
        <div className="home-nav-left">
          {canShowBackButton && (
            <button
              type="button"
              className="home-nav-back"
              onClick={handleBackToAccounts}
              disabled={isReturningToParent || isTeamSwitching}
              title={
                isReturningToParent
                  ? 'Returning…'
                  : isSubWorldSession
                    ? 'Back to main account'
                    : 'Back to accounts'
              }
              aria-label={isSubWorldSession ? 'Back to main account' : 'Back to accounts'}
            >
              {isReturningToParent ? (
                <span className="home-nav-back-spinner" aria-hidden />
              ) : (
                <svg
                  className="home-nav-back-arrow"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden
                >
                  <line x1="19" y1="12" x2="5" y2="12" />
                  <polyline points="12 19 5 12 12 5" />
                </svg>
              )}
            </button>
          )}
          <div className="home-nav-logo">
            <img src="/img/BonusClub_Logo.webp" alt="BonusClub" className="home-logo-image" />
          </div>
        </div>
        
        <div className="home-nav-profile">
          <TeamSwitcher />
          
          <div className="profile-section-wrapper" onClick={toggleProfileMenu}>
            <img 
              src="/img/menu_1.svg" 
              alt="Menu" 
              className={`profile-pic-2 ${showProfileMenu ? 'profile-pic-rotate' : ''}`}
            />
            <img 
              src={userPhoto || "/img/user-three.svg"}
              alt="Profile" 
              className="profile-pic"
              onError={(e) => { e.target.src = "/img/user-three.svg"; }}
            />
            {showProfileMenu && (
              <div className="profile-menu">
                <div className="profile-menu-info">
                  <div className="profile-menu-item-2">{userName}</div>
                  <div className="profile-menu-item-3">{auth.currentUser?.email}</div>
                </div>

                <div className="dashboard-menu-slice">
                  <img src="/img/edit.svg" alt="link settings" />
                  <NavLink to="/user-settings">Edit Profile</NavLink>
                </div>

                <div className="dashboard-menu-slice" style={{marginTop: '-5px'}}>
                  <img src="/img/mailbox.svg" alt="link settings" />
                  <a href="https://bonusclub.io" target="_blank" rel="noopener noreferrer">Contact Us</a>
                </div>

                <NavLink className="sign_out" to="/login" onClick={handleSignOut}>Sign Out</NavLink>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="home-header-section">
        <h1 className="home-title"><span className="home-title-highlight">BonusClub</span></h1>
        <p className="home-subtitle">Choose your destination</p>
      </div>
      
      <div className="home-content-wrapper">
        <div className="home-navigation-containers">
        
        {/* SalesGod World */}
        {!hasLeadsOnlyAccess && permissions.hasSalesGod && (
        <div 
          className="home-nav-container world-container"
          onClick={handleNavigateToWorld}
          style={{ cursor: 'pointer' }}
        >
          <div className="home-nav-icon icon-world">
           <img src="/img/star.svg" alt="SalesGod" className="home-logo-image" style={{filter: 'invert(1)', width: '40px', height: '40px'}}/>
          </div>
          <div className="home-nav-content">
            <div className="home-nav-header">
              <h3 className="home-nav-title">SalesGod</h3>
              <span className="home-nav-badge">AI</span>
            </div>
            <p className="home-nav-description">
              Comprehensive business management platform with email automation, lead generation, and advanced analytics for scaling your operations.
            </p>
            <div className="home-nav-features">
              <span className="home-nav-feature">
                <i className="fas fa-envelope-open-text"></i>
                Email Automation
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-user-plus"></i>
                Lead Generation
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-chart-bar"></i>
                Analytics
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-users"></i>
                CRM
              </span>
            </div>
          </div>
          <div className="home-nav-arrow">→</div>
        </div>
        )}

        {/* SalesGod Swipe Container */}
        {permissions.hasSalesGod && (
        <div 
          className="home-nav-container leadswipe-container"
          onClick={() => navigateWithTransition('/leadswipe')}
          style={{ cursor: 'pointer' }}
        >
          <div className="home-nav-icon icon-leadswipe">
            <img src="/img/star.svg" alt="LeadSwipe" className="home-logo-image" style={{filter: 'invert(1)', width: '40px', height: '40px'}}/>
          </div>
          <div className="home-nav-content">
            <div className="home-nav-header">
              <h3 className="home-nav-title">SalesGod <span style={{fontWeight: 300}}>Swipe</span></h3>
              <span className="home-nav-badge">AI</span>
            </div>
            <p className="home-nav-description">
              Swipe through your leads Tinder-style. Execute flow steps, manage favorites, and keep your pipeline moving — fast and intuitive.
            </p>
            <div className="home-nav-features">
              <span className="home-nav-feature">
                <i className="fas fa-hand-pointer"></i>
                Swipe Actions
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-heart"></i>
                Favorites
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-fire"></i>
                Flow Execution
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-mobile-alt"></i>
                Mobile Ready
              </span>
            </div>
          </div>
          <div className="home-nav-arrow">→</div>
        </div>
        )}

        {/* Pages Container */}
        {!isTeamMemberWithLimitedAccess && permissions.hasSalesPages && (
        <div 
          className="home-nav-container pages-container"
          onClick={handleNavigateToPages}
          style={{ cursor: 'pointer' }}
        >
          <div className="home-nav-icon icon-pages">
         <img src="/img/abstract_1.svg" alt="Web Studio" className="home-logo-image" style={{filter: 'invert(1)', width: '40px', height: '40px'}}/>
          </div>
          <div className="home-nav-content">
            <div className="home-nav-header">
              <h3 className="home-nav-title">SalesPage</h3>
              <span className="home-nav-badge">AI</span>
            </div>
            <p className="home-nav-description">
              Create stunning websites with our AI-powered page builder. Generate professional layouts, content, and designs in seconds.
            </p>
            <div className="home-nav-features">
              <span className="home-nav-feature">
                <i className="fas fa-wand-magic-sparkles"></i>
                Websites
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-puzzle-piece"></i>
               Online Shops
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-globe"></i>
                Funnels
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-chart-line"></i>
                Digital Products
              </span>
            </div>
          </div>
          <div className="home-nav-arrow">→</div>
        </div>
        )}

        {/* XRAAY Container */}
        {!isTeamMemberWithLimitedAccess && permissions.hasXRAAY && (
        <div 
          className="home-nav-container xray-container"
          onClick={handleNavigateToXray}
          style={{ cursor: 'pointer' }}
        >
          <div className="home-nav-icon icon-xray">
         <img src="/img/x.png" alt="XRAAY" className="home-logo-image" style={{filter: 'invert(1)', width: '40px', height: '40px'}}/>
          </div>
          <div className="home-nav-content">
            <div className="home-nav-header">
              <h3 className="home-nav-title">XRAAY</h3>
              <span className="home-nav-badge">AI</span>
            </div>
            <p className="home-nav-description">
              Comprehensive business analysis tool with intelligent scoring, strategic recommendations, and detailed insights for growth optimization.
            </p>
            <div className="home-nav-features">
              <span className="home-nav-feature">
                <i className="fas fa-chart-pie"></i>
                Business Score
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-lightbulb"></i>
                AI Insights
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-bullseye"></i>
                Strategy
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-rocket"></i>
                Growth Plan
              </span>
            </div>
          </div>
          <div className="home-nav-arrow">→</div>
        </div>
        )}

        {/* Deal Wizard Container */}
        {hasDealsAccess && (
        <div 
          className="home-nav-container dealwizard-container"
          onClick={handleNavigateToDealWizard}
          style={{ cursor: 'pointer' }}
        >
          <div className="home-nav-icon icon-dealwizard">
            <img src="/img/bag.svg" alt="Deal Wizard" className="home-logo-image" style={{filter: 'invert(1)', width: '40px', height: '40px'}}/>
          </div>
          <div className="home-nav-content">
            <div className="home-nav-header">
              <h3 className="home-nav-title">Deals</h3>
              <span className="home-nav-badge">AI</span>
            </div>
            <p className="home-nav-description">
              Create irresistible offers and deals with AI-powered pricing strategies and conversion optimization.
            </p>
            <div className="home-nav-features">
              <span className="home-nav-feature">
                <i className="fas fa-tags"></i>
                Smart Pricing
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-magic"></i>
                Offer Builder
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-percent"></i>
                Discounts
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-bolt"></i>
                Flash Deals
              </span>
            </div>
          </div>
          <div className="home-nav-arrow">→</div>
        </div>
        )}

        {/* Academy Container */}
        {!isTeamMemberWithLimitedAccess && permissions.hasAcademy && (
        <div 
          className="home-nav-container academy-container"
          onClick={handleNavigateToAcademy}
          style={{ cursor: 'pointer' }}
        >
          <div className="home-nav-icon icon-academy">
            <img src="/img/certif.svg" alt="Academy" className="home-logo-image" style={{filter: 'invert(1)', width: '40px', height: '40px'}}/>
          </div>
          <div className="home-nav-content">
            <div className="home-nav-header">
              <h3 className="home-nav-title">Academy</h3>
            </div>
            <p className="home-nav-description">
              Master sales, marketing, and business growth with exclusive video courses and expert tutorials.
            </p>
            <div className="home-nav-features">
              <span className="home-nav-feature">
                <i className="fas fa-video"></i>
                Courses
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-graduation-cap"></i>
                Certifications
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-book-open"></i>
                Tutorials
              </span>
              <span className="home-nav-feature">
                <i className="fas fa-star"></i>
                Expert Tips
              </span>
            </div>
          </div>
          <div className="home-nav-arrow">→</div>
        </div>
        )}

        </div>
      </div>
    </div>
  );
};

export default Home;
